import Immutable from 'immutable';
import React from 'react';

import GraphQL from '~/types/graphql';

import AdminAddGuaranteedBlacklistDomainsModal from '~/components/app/AdminAddGuaranteedBlacklistDomainsModal';
import BasicIcon, {
	BasicIconType,
} from '~/components/patterns/icons/BasicIcon';
import Button, {
	ButtonSize,
	ButtonStyle,
} from '~/components/patterns/buttons/Button';
import ButtonsGroup from '~/components/patterns/buttons/ButtonsGroup';
import Ellipsis from '~/components/patterns/values/Ellipsis';
import NewDatatable, {
	type NewDatatableColumnDefinitions,
} from '~/components/app/NewDatatable';
import PlainTextFieldFilter from '~/components/logic/datatables/PlainTextFieldFilter';
import WithPermission from '~/components/logic/access/WithPermission';

import {
	useKingdomSettingsGuaranteedBlacklistDomainsQuery,
} from './KingdomSettingsGuaranteedBlacklistDomainsSection.gql';

import useOpenModal from '~/hooks/useOpenModal';

import {
	ObjectType,
} from '~/model/permissions';



const KingdomSettingsGuaranteedBlacklistDomainsSection: React.FC = () => {
	const [filter, setFilter] = React.useState(Immutable.Map({
		blacklistDomain: '',
	}));

	const { data } = useKingdomSettingsGuaranteedBlacklistDomainsQuery();

	const guaranteedBlacklistDomains = data?.kingdomSettings?.guaranteedBlacklistDomains ?? null;

	const handleOpenAddDomainsModal = useOpenModal(
		() => (
			<AdminAddGuaranteedBlacklistDomainsModal />
		),
		[],
	);

	const updateFilter = React.useCallback(
		(filterUpdate) => {
			setFilter((filter) => filter.merge(filterUpdate));
		},
		[],
	);

	const rows = React.useMemo(
		() => {
			return (guaranteedBlacklistDomains ?? []).filter(
				(row) => {
					return row.includes(filter.get('blacklistDomain'));
				},
			);
		},
		[
			filter,
			guaranteedBlacklistDomains,
		],
	);

	const columns: NewDatatableColumnDefinitions<typeof rows> = React.useMemo(
		() => {
			return [
				{
					name: 'blacklistDomain',
					render: {
						cell: ({ row }) => (
							<Ellipsis>
								{row}
							</Ellipsis>
						),
						filter: ({ filterName, filterWidth, ref }) => (
							<PlainTextFieldFilter
								name={filterName}
								ref={ref}
								width={filterWidth}
							/>
						),
						label: () => 'Domain',
					},
					width: 'fill',
				},
			];
		},
		[],
	);

	return (
		<NewDatatable
			buttons={(
				<ButtonsGroup>
					<WithPermission
						action={GraphQL.ActionWithPlatform.ManageInternals}
						objectId="platform"
						objectType={ObjectType.Platform}
						showMessage={false}
					>
						{({ isAllowed, message: permissionMessage }) => (
							<Button
								disabled={isAllowed.yes === false}
								icon={(
									<BasicIcon type={BasicIconType.Plus} />
								)}
								onClick={handleOpenAddDomainsModal}
								size={ButtonSize.Small}
								style={ButtonStyle.Hollow}
								tooltip={permissionMessage}
								uppercase={true}
							>
								Add domains
							</Button>
						)}
					</WithPermission>
				</ButtonsGroup>
			)}
			columns={columns}
			filter={filter}
			isLoading={guaranteedBlacklistDomains === null}
			onFilterChange={updateFilter}
			rows={rows}
			title="Guaranteed blacklist domains"
		/>
	);
};



export default KingdomSettingsGuaranteedBlacklistDomainsSection;
